import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CheckCircle2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import VisitorPrizeNotice from "@/components/VisitorPrizeNotice";
import { API } from "@/lib/constants";

export default function VisitorThankYou() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").toUpperCase();
  const dup = params.get("dup") === "1";
  const previewPrize = params.get("preview_prize") === "1";
  const [detail, setDetail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [prizeCfg, setPrizeCfg] = useState(null);
  const arFont = isRTL
    ? { fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }
    : undefined;

  const handleCopyRef = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(ref);
      } else {
        // iOS Safari <13 / non-secure context fallback
        const ta = document.createElement("textarea");
        ta.value = ref; ta.setAttribute("readonly", ""); ta.style.position = "absolute"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success(t("visitorTyCopied"));
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {
      toast.error(t("visitorTyCopyFailed"));
    }
  };

  useEffect(() => {
    if (!ref) return;
    axios.get(`${API}/visitor/lookup/${encodeURIComponent(ref)}`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null));
  }, [ref]);

  // Rev 7.7 — fetch prize terms; on confirmation we mirror the block
  // when the flag is ON or when ?preview_prize=1 is set for owner
  // review.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/visitor/prize-terms`)
      .then((r) => { if (!cancelled) setPrizeCfg(r.data); })
      .catch(() => { if (!cancelled) setPrizeCfg({ enabled: false }); });
    return () => { cancelled = true; };
  }, []);
  const prizeVisible = !!(prizeCfg && (prizeCfg.enabled || previewPrize));

  if (!ref) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <button onClick={() => navigate("/")} className="btn-primary">{t("visitorTyHome")}</button>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden" dir={isRTL ? "rtl" : "ltr"}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1000px 500px at 50% -10%, rgba(245,197,24,0.10), transparent 60%)" }} />
      <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-8 pt-6 pb-16">
        <div className={`flex items-center justify-end mb-4`}>
          <LanguageToggle />
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div
            className={`inline-flex items-center gap-2 text-[#F5C518] px-4 py-2 border border-[#F5C518]/50 bg-[#F5C518]/10 ${isRTL ? "flex-row-reverse" : ""}`}
            data-testid="visitor-ty-status-badge"
          >
            <CheckCircle2 className="w-6 h-6 shrink-0" />
            <span
              className={
                isRTL
                  ? "text-base sm:text-lg font-extrabold"
                  : "text-base sm:text-lg font-extrabold tracking-[0.15em] uppercase"
              }
              style={arFont}
            >
              {t("regClosedTitle")}
            </span>
          </div>
          <h1
            className={`mt-3 font-heading font-extrabold text-white ${isRTL ? "text-3xl sm:text-5xl" : "text-3xl sm:text-4xl"}`}
            style={arFont}
            data-testid="visitor-ty-title"
          >
            {t("visitorTyTitle")}
          </h1>
          <p className={`mt-4 text-white/90 leading-relaxed text-lg sm:text-xl font-medium`} style={arFont}>
            {t("visitorTyBody")}
          </p>
          {dup && (
            <p className="mt-2 text-amber-400 text-base font-semibold" data-testid="visitor-ty-dup" style={arFont}>{t("visitorTyDupNote")}</p>
          )}

          <div className="mt-6 border-2 border-[#F5C518]/60 bg-[#F5C518]/[0.08] p-6 sm:p-8" data-testid="visitor-ty-ref-block">
            <div className="text-lg sm:text-xl text-[#F5C518] font-bold" style={arFont}>{t("visitorTyRefLabel")}</div>
            <div
              className="mt-3 font-mono font-extrabold text-[#F5C518] text-4xl sm:text-5xl tracking-widest select-all"
              dir="ltr"
              data-testid="visitor-ty-ref"
            >
              {ref}
            </div>
            <div className={`mt-4 flex flex-wrap items-center gap-3 ${isRTL ? "justify-end" : "justify-start"}`}>
              <button
                type="button"
                onClick={handleCopyRef}
                data-testid="visitor-ty-copy-btn"
                className={`inline-flex items-center gap-2 px-5 py-3 min-h-[48px] border ${copied ? "border-[#F5C518] text-[#F5C518]" : "border-white/40 text-white hover:border-[#F5C518] hover:text-[#F5C518]"} text-lg font-semibold transition-colors`}
                style={arFont}
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                <span>{copied ? t("visitorTyCopied") : t("visitorTyCopy")}</span>
              </button>
            </div>
            <div className="mt-4 text-white text-lg sm:text-xl leading-relaxed font-semibold" style={arFont}>{t("visitorTyEmailReassure")}</div>
          </div>

          {/* Rev 7.8 — Consolidated notice card. Instead of the previous
              two separate notices (neutral + prize) which felt cluttered
              on mobile, the two are merged into ONE card. The neutral
              group-ticket line always shows; the prize-claim clarification
              appears below only when prizeVisible (flag on or preview
              query). Consistent typography ≥18px bilingual bold. */}
          <div
            className={`mt-6 border-2 ${prizeVisible ? "border-[#F5C518] bg-black" : "border-[#F5C518] bg-[#F5C518]/10"} px-5 py-5 sm:px-6 sm:py-6`}
            data-testid="visitor-ty-group-ticket-notice"
          >
            {prizeVisible && previewPrize && !prizeCfg?.enabled && (
              <div className="mb-3 inline-block bg-amber-500 text-black text-[11px] font-extrabold uppercase tracking-widest px-3 py-1" data-testid="visitor-ty-prize-preview-badge">
                Draft · Not Public · owner preview
              </div>
            )}
            <p
              dir="ltr"
              className="text-white font-bold text-lg sm:text-xl leading-snug"
              data-testid="visitor-ty-group-ticket-notice-en"
            >
              {t("visitorGroupTicketPublicEN")}
            </p>
            <div className="h-2" />
            <p
              lang="ar"
              dir="rtl"
              className="text-white font-bold text-lg sm:text-xl leading-snug text-right"
              style={{ fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }}
              data-testid="visitor-ty-group-ticket-notice-ar"
            >
              {t("visitorGroupTicketPublicAR")}
            </p>

            {prizeVisible && (
              <div className="mt-5 pt-5 border-t border-[#F5C518]/40" data-testid="visitor-ty-prize-block">
                <p
                  dir="ltr"
                  className="text-white font-extrabold leading-relaxed text-lg sm:text-2xl"
                  data-testid="visitor-ty-prize-notice-en"
                >
                  {prizeCfg?.en || ""}
                </p>
                <div className="h-3" />
                <p
                  lang="ar"
                  dir="rtl"
                  className="text-white font-extrabold leading-relaxed text-xl sm:text-2xl text-right"
                  style={{ fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }}
                  data-testid="visitor-ty-prize-notice-ar"
                >
                  {prizeCfg?.ar || ""}
                </p>
              </div>
            )}
          </div>

          {/* Feb 2026 owner-approved Visitor Prize Draw NOTICE
              (informational only; does not activate the draw). */}
          <VisitorPrizeNotice className="mt-6" />

          {detail && (detail.planned_days?.length > 0 || detail.total_attending) && (
            <div className="mt-4 border border-white/10 bg-white/[0.02] p-4 text-base text-white/85" data-testid="visitor-ty-detail" style={arFont}>
              {detail.total_attending != null && (
                <div>{t("visitorTotalAttending")}: <span className="text-white font-semibold">{detail.total_attending}</span></div>
              )}
              {detail.planned_days?.length > 0 && (
                <div className="mt-1">
                  {t("visitorPlannedDays").split("(")[0]}:
                  <span className="text-white ms-1 font-semibold">
                    {detail.planned_days.map((d) => t(`visitorDay_${d}`)).join(" · ")}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className={`mt-8 flex ${isRTL ? "justify-start" : "justify-end"}`}>
            <button
              onClick={() => navigate("/")}
              className="btn-primary group inline-flex items-center gap-3"
              data-testid="visitor-ty-home-btn"
            >
              {t("visitorTyHome")}
              {isRTL
                ? <ArrowRight className="w-4 h-4 rotate-180 transition-transform group-hover:-translate-x-1" />
                : <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
